import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { DEFAULT_INDICATOR_COLOR, DEFAULT_INDICATOR_FONT_SIZE_XS, DEFAULT_INDICATOR_LINE_WIDTH } from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface DoorLengthIndicatorProps {
  doorWidth: number;
  wallSideALength: number;
  rotation: number; // Added rotation prop
  color?: string;
  lineWidth?: number;
}

const DoorLengthIndicator = ({
  doorWidth,
  wallSideALength,
  rotation,
  color = DEFAULT_INDICATOR_COLOR,
  lineWidth = DEFAULT_INDICATOR_LINE_WIDTH,
}: DoorLengthIndicatorProps): React.JSX.Element => {
  const { locale } = useLanguage();
  const { unitType } = useUnitContext();
  const intl = useIntl();
  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [locale]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [locale]);

  const newA = new THREE.Vector3(-doorWidth / 2, 0, 0);
  const newB = new THREE.Vector3(doorWidth / 2, 0, 0);
  const sideAlengthText = useMemo(
    () =>
      unitType === EUnitType.METRIC
        ? localizeNumber(wallSideALength.toFixed(2), locale) + cmLocalizedLabel
        : localizeNumber(convertTo(wallSideALength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
          inLocalizedLabel,
    [locale, unitType, wallSideALength],
  );

  return (
    <group name={'group-doorLengthIndicator'} position={[0, 0, 1]} renderOrder={RENDER_ORDERS.lengthIndicator}>
      {/* Door length indicator line */}
      <Line name={'doorLengthIndicator-line'} points={[newA, newB]} position={[0, 0, -0.5]} color={color} lineWidth={lineWidth} />
      {/* Text group with negated rotation to counteract parent rotation */}
      <group rotation={[0, 0, -rotation]}>
        <RoundedBoxWithText boxSize={[3, 1, 0]} position={new THREE.Vector3(0, 0, 0)} fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS} textColor="black" text={sideAlengthText} />
      </group>
    </group>
  );
};

export default DoorLengthIndicator;
